import Api from "./Api";
import {
  DashboardExpensesToProcess,
  DashboardLayout,
  DashboardOverdueInvoices,
  DashboardProjectBudgetHealth,
  DashboardUnbilledTimeEntries,
  DashboardUpcomingRecurringInvoices,
  UserWorkspaceSetting,
} from "../types";

export interface DashboardLayoutErrorResponse {
  errors: { field: string; message: string }[];
}

export default class DashboardController extends Api {
  static getMonthlySummaries() {
    return fetch(`/api/dashboard/monthly_summaries`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getYearlyRevenueGoal() {
    return fetch(`/api/dashboard/yearly_revenue_goal`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getMonthlyRevenue() {
    return fetch(`/api/dashboard/monthly_revenue`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getAccountReceivables() {
    return fetch(`/api/dashboard/account_receivables`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getExpenses() {
    return fetch(`/api/dashboard/expenses`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getFinancialYearSummary(financialYearId?: string) {
    const params = new URLSearchParams();
    if (financialYearId) params.set("financial_year_id", financialYearId);
    const query = params.toString();
    return fetch(
      `/api/dashboard/financial_year_summary${query ? `?${query}` : ""}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: this.token,
        },
        credentials: "same-origin",
      },
    ).then((res) => res.json());
  }

  static getQuarterSummary() {
    return fetch(`/api/dashboard/quarter_summary`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getSentInvoicesMonthly() {
    return fetch(`/api/dashboard/sent_invoices_monthly`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getOverdueInvoices(): Promise<DashboardOverdueInvoices> {
    return fetch(`/api/dashboard/overdue_invoices`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getUpcomingRecurringInvoices(days: number): Promise<DashboardUpcomingRecurringInvoices> {
    return fetch(`/api/dashboard/upcoming_recurring_invoices?days=${days}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getExpensesToProcess(): Promise<DashboardExpensesToProcess> {
    return fetch(`/api/dashboard/expenses_to_process`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getProjectBudgetHealth(): Promise<DashboardProjectBudgetHealth> {
    return fetch(`/api/dashboard/project_budget_health`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static getUnbilledTimeEntries(): Promise<DashboardUnbilledTimeEntries> {
    return fetch(`/api/dashboard/unbilled_time_entries`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
    }).then((res) => res.json());
  }

  static updateLayout(
    layout: DashboardLayout | null,
  ): Promise<UserWorkspaceSetting | DashboardLayoutErrorResponse> {
    return fetch(`/api/dashboard/layout`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: this.token,
      },
      credentials: "same-origin",
      body: JSON.stringify({ dashboard_layout: layout }),
    }).then(async (res) => {
      const body = await res.json().catch(() => null);
      if (res.ok && body) return body as UserWorkspaceSetting;
      return { errors: body?.errors ?? [] };
    });
  }
}
